import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowUpRight, PhoneCall } from "lucide-react";
import axios from "axios";
import { CATEGORIES, CATEGORY_LABELS, FALLBACK_RESOURCES } from "@/data/resources";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Resources() {
  const [items, setItems] = useState(FALLBACK_RESOURCES);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("alle");

  useEffect(() => {
    axios.get(`${API}/resources`)
      .then((res) => {
        if (Array.isArray(res.data?.resources) && res.data.resources.length) {
          setItems(res.data.resources);
        }
      })
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((r) => {
      const inCategory = category === "alle" || r.category === category;
      const inQuery = !q || `${r.title} ${r.description}`.toLowerCase().includes(q);
      return inCategory && inQuery;
    });
  }, [items, query, category]);

  return (
    <section id="ressurser" data-testid="resource-directory-section" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.2em] font-mono font-semibold text-terracotta">Ressurskatalog</p>
          <h2 className="mt-3 font-serif font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-primary">
            Offentlige sider, skjemaer og registre
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
            Alle lenkene går til offisielle norske sider. Søk eller filtrer for å finne det du trenger.
          </p>
        </motion.div>

        <div className="mt-10 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-sm">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="resource-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Søk etter hjelp, skjema eller register …"
              className="w-full bg-surface border border-border rounded-full pl-11 pr-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/15 transition-all duration-200"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                data-testid={c.testid}
                onClick={() => setCategory(c.id)}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 ${
                  category === c.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-accent text-accent-foreground hover:bg-primary/15"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <motion.div layout className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((r) => (
              <motion.article
                layout
                key={r.id}
                data-testid="resource-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="group bg-surface border border-border rounded-2xl p-6 flex flex-col hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300"
              >
                <span className="self-start text-[11px] font-mono font-semibold uppercase tracking-[0.15em] bg-accent text-accent-foreground px-3 py-1.5 rounded-full">
                  {CATEGORY_LABELS[r.category] || r.category}
                </span>
                <h3 className="mt-4 text-lg font-semibold leading-snug">{r.title}</h3>
                <p className="mt-2.5 text-sm text-muted-foreground leading-relaxed flex-1">{r.description}</p>
                <div className="mt-6 flex items-center justify-between gap-3">
                  {r.phone ? (
                    <a href={`tel:${r.phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1.5 text-xs font-mono font-semibold text-muted-foreground hover:text-foreground transition-colors">
                      <PhoneCall size={12} /> {r.phone}
                    </a>
                  ) : <span />}
                  <a
                    data-testid="resource-external-link"
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => fetch(`${API}/track`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ type: "click", resource_id: r.id }),
                      keepalive: true,
                    }).catch(() => {})}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-transform duration-200 group-hover:translate-x-0.5"
                  >
                    Åpne side <ArrowUpRight size={15} />
                  </a>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        {filtered.length === 0 && (
          <p data-testid="resource-empty-state" className="mt-12 text-center text-muted-foreground text-sm">
            Ingen treff — prøv et annet søkeord eller velg «Alle».
          </p>
        )}
      </div>
    </section>
  );
}
